import { useMemo } from "react";
import { View } from "react-native";
import RoundedButton from "src/components/RoundedButton";
import log from "src/helpers/log";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { paddingLarge, paddingMedium, paddingSmallSpacer, paddingXXXL } from "src/styles/spacing";
import { Text } from "src/swsh-native";
import useWebImageDownload from "../../hooks/useWebImageDownload.web";
import SaveTutorialImage from "../SaveTutorialImage";
import { SaveTutorialModalContentProps } from "./types";

const SaveTutorialModalContent = ({
	data,
	onComplete,
	onShareCancel,
	saveVariant = "image",
	closeSelf,
	onError,
}: SaveTutorialModalContentProps) => {
	const { loading, handleDownloadImages } = useWebImageDownload(data);
	const handlePress = async () => {
		try {
			const { didComplete, didError } = await handleDownloadImages();
			closeSelf?.();
			if (didComplete) {
				onComplete?.();
			} else if (didError) {
				onError?.();
			} else {
				onShareCancel?.();
			}
		} catch (err) {
			log.error({ err }, "Error downloading");
			onError?.();
		}
	};

	const { saveText, contentSaved } = useMemo<{
		saveText: string;
		contentSaved: string;
	}>(() => {
		const isPlural = data.length > 1;
		switch (saveVariant) {
			case "image":
				if (isPlural) {
					return {
						saveText: `Save ${data.length} Images`,
						contentSaved: "photos",
					};
				} else {
					return {
						saveText: "Save Image",
						contentSaved: "photo",
					};
				}
			case "video":
				if (isPlural) {
					return {
						saveText: `Save ${data.length} Videos`,
						contentSaved: "videos",
					};
				} else {
					return {
						saveText: "Save Video",
						contentSaved: "video",
					};
				}
			case "imageAndVideo":
				if (isPlural) {
					return {
						saveText: `Save ${data.length} Items`,
						contentSaved: "photos and videos",
					};
				} else {
					return {
						saveText: "Save Item",
						contentSaved: "photo or video",
					};
				}
			default:
				return enumNever(saveVariant);
		}
	}, [data.length, saveVariant]);

	return (
		<View
			style={{
				flex: 1,
				minHeight: 500,
				height: "100%",
				maxWidth: "100%",
			}}
		>
			<View
				style={{
					alignItems: "center",
					justifyContent: "center",
					paddingHorizontal: paddingSmallSpacer,
				}}
			>
				<Text
					large
					italic
					semibold
					style={{
						textAlign: "center",
					}}
				>
					Saving memories to your camera roll
				</Text>
				<Text
					color="TextLight"
					small
					style={{
						marginTop: paddingMedium,
						textAlign: "center",
					}}
				>
					Tap “{saveText}” to save your {contentSaved}.
				</Text>
			</View>
			<View
				style={{
					flex: 1,
					width: "100%",
					paddingHorizontal: paddingXXXL,
					marginTop: paddingLarge,
					alignItems: "center",
				}}
			>
				<SaveTutorialImage
					saveText={saveText}
					onTextPress={() => {
						if (!loading) {
							handlePress();
						}
					}}
				/>
				<RoundedButton
					onPress={handlePress}
					sizeVariant="Large"
					colorVariant="Contrast"
					loading={loading}
				>
					Got it
				</RoundedButton>
			</View>
		</View>
	);
};
export default SaveTutorialModalContent;
